import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Company } from '../../../rent/model/company';
import { IncorrectPayment } from '../../model/incorrect-payment';
import { IncorrectPaymentTransaction } from '../../model/incorrect-payment-transaction';
import { InvoicePeriodItem } from '../../model/invoice-period';
import { VoidPaymentModel } from '../../model/void-payment';

@Injectable()
export class IncorrectPaymentService extends DataService<IncorrectPayment> {
  private readonly _defaultFilterFields: string[] = ['company', 'fromDate', 'includeHandled', 'maxBalance', 'payerName', 'balanceOnDate'];

  constructor(client: HttpClient, messageService: MessageService, progressBar: ProgressBarService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(client, config, messageService, IncorrectPayment, progressBar, 'incorrect-payment');
  }

  protected createModel(data: any): IncorrectPayment {
    const model = super.createModel(data);
    if (data.incorrectPaymentTransactions instanceof Array) {
      model.incorrectPaymentTransactions = data.incorrectPaymentTransactions.map(item =>
        Object.assign(new IncorrectPaymentTransaction(), item)
      );
    }
    return model;
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    fromDate: string;
    maxBalance: Money;
    includeHandled: boolean;
    dateLimits: InvoicePeriodItem[];
    payerName: string;
    balanceOnDate: string;
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : '',
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          fromDate: res.fromDate ? new LocalDate(res.fromDate.year, res.fromDate.month, 1).toISOString() : res.fromDate,
          maxBalance: res.maxBalance,
          includeHandled: res.includeHandled,
          dateLimits: Array.isArray(res.dateLimits) ? res.dateLimits.map(x => Object.assign(new InvoicePeriodItem(), x)) : [],
          payerName: res.payerName ? res.payerName : '',
          balanceOnDate: res.balanceOnDate
            ? new LocalDate(
                res.balanceOnDate.year,
                res.balanceOnDate.month,
                LocalDate.daysInMonth(res.balanceOnDate.year, res.balanceOnDate.month)
              ).toISOString()
            : res.balanceOnDate,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  exportFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Felaktiga inbetalningar.xlsx', true);
  }

  mapFilterValue(key: string, value: any): any {
    if (key === 'fromDate' && value) return LocalDate.fromDate(value);
    if (key === 'balanceOnDate' && value) return LocalDate.fromDate(value);
    return super.mapFilterValue(key, value);
  }

  payInvoice(
    amount: Money,
    paymentId: string,
    invoiceId: string,
    businessTransactionDate: string,
    companyBankAccountId?: string
  ): Observable<IncorrectPayment> {
    this.progressBar.start();
    return this.client
      .put<IncorrectPayment>(
        `${this.endpoint}/pay-invoice`,
        JSON.stringify({
          amount: amount,
          id: paymentId,
          invoice: invoiceId,
          businessTransactionDate: businessTransactionDate,
          companyBankAccountId: companyBankAccountId,
        })
      )
      .pipe(
        map(data => Object.assign(new IncorrectPayment(), data)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  addToContactBalance(
    amount: Money,
    paymentId: string,
    contactId: string,
    leaseAgreementId: string,
    businessTransactionDate: LocalDate
  ): Observable<IncorrectPayment> {
    this.progressBar.start();
    return this.client
      .put<IncorrectPayment>(
        `${this.endpoint}/add-balance`,
        JSON.stringify({
          amount: amount,
          id: paymentId,
          contactId: contactId,
          leaseAgreementId: leaseAgreementId,
          businessTransactionDate: businessTransactionDate,
        })
      )
      .pipe(
        map(data => Object.assign(new IncorrectPayment(), data)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  update(payment: IncorrectPayment): Observable<IncorrectPayment> {
    this.progressBar.start();
    return this.client.put<IncorrectPayment>(this.endpoint, JSON.stringify({ id: payment.id, payer: payment.payer })).pipe(
      map(data => Object.assign(new IncorrectPayment(), data)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  voidPayment(model: VoidPaymentModel): Observable<IncorrectPayment> {
    this.progressBar.start();
    return this.client
      .post<IncorrectPayment>(`${this.endpoint}/void-payment`, JSON.stringify(model), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map(data => Object.assign(new IncorrectPayment(), data)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
    // TODO... what is this code doing?? Backend doesn't return anything!
    // PS: void-payment-command-handler expects a non-null return value - so must return something here!
  }

  voidIncorrectPayments(ids: string[]): Observable<any> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/void-incorrect-payments`, JSON.stringify(ids), { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  voidIncorrectPaymentsFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<any> {
    if (includedIds.length > 0) {
      return this.voidIncorrectPayments(includedIds);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/void-incorrect-payments-filter`,
        { filter: this.prepareFilter(filter), includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsCount },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

}
